import { RiLoader4Line, RiShoppingCartLine, RiTimeLine } from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { $api } from "@/api/query";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { countdownClass, formatCountdown } from "@/lib/countdown";

/**
 * Store app: Store Orders.
 *
 * An order routed to this store is OFFERED, not owned: it must be accepted
 * before the countdown runs out or it is retargeted to another store. The
 * countdown is therefore the most prominent thing on each card.
 *
 * A line-level offer (order split across stores) always renders its sibling
 * lines, greyed and non-actionable, so a store never ships a fragment without
 * seeing that another shop is picking the rest.
 */
export const Route = createFileRoute("/_app/store/orders")({
	component: StoreOrdersPage,
});

type Tab = "pending" | "accepted" | "lost" | "all";

interface StoreOrder {
	id: string;
	orderCode: string;
	orderNumber: string | null;
	fulfillmentStatus: string;
	branchNumber: number | null;
	storeOfferedAt: string | null;
	storeAcceptedAt: string | null;
	storeOfferExpiresAt: string | null;
	dateCreated: string;
	customerNickname: string | null;
	totalWithShip: string | null;
	currency: string | null;
	lineCount: number;
	storeLineCount: number;
	offerLevel: "order" | "line";
}

interface OrderLine {
	id: string;
	barcode: string;
	size: string;
	branchNumber: number | null;
	storeOfferedAt: string | null;
	storeAcceptedAt: string | null;
	storeOfferExpiresAt: string | null;
	storeName: string | null;
	sku: string | null;
	isThisStore: boolean;
}

interface OrderDetail extends StoreOrder {
	lines: OrderLine[];
}

/// Poll interval for the store order list. Matches the admin Orders page.
const STORE_ORDERS_POLL_MS = 30_000;
function StoreOrdersPage() {
	const queryClient = useQueryClient();
	const [tab, setTab] = useState<Tab>("pending");
	const [expanded, setExpanded] = useState<string | null>(null);
	const [declineTarget, setDeclineTarget] = useState<StoreOrder | null>(null);
	const [inFlight, setInFlight] = useState<string | null>(null);

	// An admin has no store of their own, so the store-scoped calls 400 until a
	// store is picked. Same picker as the My Store page rather than a second
	// pattern. A store user never sees it: their scope is fixed and no request
	// parameter can change it.
	const [storeId, setStoreId] = useState<string | null>(null);
	const adminStores = $api.useQuery("get", "/api/v1/stores" as never);
	const storeOptions = (adminStores.data as { id: string; name: string }[] | undefined) ?? [];

	// EVERY store-scoped call needs the admin's picked store, not just the list.
	// Omitting it on the detail/accept/decline calls made them 400 for an admin
	// while the list rendered fine - the page looked healthy and did nothing.
	// One helper so a new call site cannot forget it.
	const scopeQs = storeId ? `?storeId=${storeId}` : "";

	const query = $api.useQuery("get", "/api/v1/store/orders", {
		// openapi-fetch drops undefined query values, so a store user (no
		// picked store) sends `?tab=` alone.
		params: { query: { tab, storeId: storeId ?? undefined } },
		// Shop staff leave this page open and act on offers that expire (24h
		// TTL, and the countdown turns red under 1h). Without polling a new
		// offer only appears on reload, so an order can time out and be
		// retargeted while the tab sat there looking empty.
		refetchInterval: STORE_ORDERS_POLL_MS,
	});
	const orders = (query.data as { orders: StoreOrder[] } | undefined)?.orders ?? [];

	// "Pick a store" is not an error: an admin with no store selected gets a 400
	// by design (StoreScope::resolve). Showing a red failure for it taught
	// operators to distrust the page, so it renders as a prompt instead. A store
	// user has no admin list, so this stays false for them and a real failure
	// still surfaces as an error.
	//
	// This deliberately does NOT require storeOptions to be non-empty. The
	// /stores list resolves AFTER the orders 400, so gating on it painted the
	// red "Could not load store orders." banner during that window - and it
	// stayed on screen next to the successfully loaded orders once a store was
	// picked. Waiting for the list to settle is the correct derivation: while
	// it is still loading we know nothing yet, so we claim nothing.
	const needsStorePick =
		query.isError && !storeId && (adminStores.isLoading || storeOptions.length > 0);

	function refresh() {
		queryClient.invalidateQueries({ queryKey: ["get"] });
	}

	async function accept(order: StoreOrder) {
		setInFlight(order.id);
		const res = await fetch(`/api/v1/store/orders/${order.id}/accept${scopeQs}`, {
			method: "POST",
			credentials: "include",
		});
		setInFlight(null);
		if (res.ok) {
			toast.success(`Order ${order.orderCode} accepted`);
			refresh();
		} else if (res.status === 409) {
			// Someone else accepted first, or the offer expired between render and
			// click. Refresh so the card disappears instead of lying.
			toast.error("This offer is no longer available. Refreshing.");
			refresh();
		} else {
			toast.error("Could not accept the order");
		}
	}

	async function decline(order: StoreOrder) {
		setInFlight(order.id);
		const res = await fetch(`/api/v1/store/orders/${order.id}/decline${scopeQs}`, {
			method: "POST",
			credentials: "include",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({}),
		});
		setInFlight(null);
		setDeclineTarget(null);
		if (res.ok) {
			toast.success(`Order ${order.orderCode} was offered to another store`);
			refresh();
		} else {
			toast.error("Could not decline the order");
		}
	}

	async function acceptLine(orderId: string, lineId: string) {
		setInFlight(lineId);
		const res = await fetch(`/api/v1/store/orders/${orderId}/lines/${lineId}/accept${scopeQs}`, {
			method: "POST",
			credentials: "include",
		});
		setInFlight(null);
		if (res.ok) {
			toast.success("Item accepted");
			refresh();
		} else if (res.status === 409) {
			toast.error("This offer is no longer available. Refreshing.");
			refresh();
		} else {
			toast.error("Could not accept the item");
		}
	}

	return (
		<div className="space-y-6 p-4 sm:p-6">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<h1 className="flex items-center gap-2 text-xl font-semibold sm:text-2xl">
						<RiShoppingCartLine className="size-5 sm:size-6" aria-hidden />
						Store Orders
					</h1>
					<p className="text-sm text-muted-foreground">
						Accept an order before its window closes, or it is offered to another store.
					</p>
				</div>

				{storeOptions.length > 0 && (
					<Select value={storeId ?? undefined} onValueChange={(v) => setStoreId(v)}>
						<SelectTrigger className="w-full sm:w-56" aria-label="Select store">
							<SelectValue placeholder="Select a store" />
						</SelectTrigger>
						<SelectContent>
							{storeOptions.map((s) => (
								<SelectItem key={s.id} value={s.id}>
									{s.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
			</div>

			<Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
				<TabsList>
					<TabsTrigger value="pending">Needs acceptance</TabsTrigger>
					<TabsTrigger value="accepted">Accepted</TabsTrigger>
					<TabsTrigger value="lost">Lost / expired</TabsTrigger>
					<TabsTrigger value="all">All</TabsTrigger>
				</TabsList>
			</Tabs>

			{query.isLoading && (
				<div className="space-y-2">
					<Skeleton className="h-28 w-full" />
					<Skeleton className="h-28 w-full" />
				</div>
			)}

			{needsStorePick && (
				<Card>
					<CardContent className="pt-6 text-sm text-muted-foreground">
						Select a store to view its orders.
					</CardContent>
				</Card>
			)}

			{/* Never paint an error next to loaded orders: with a store picked the
			    request succeeded, and a leftover error card reads as a failure the
			    operator cannot act on. Data present wins. */}
			{query.isError && !needsStorePick && query.data === undefined && (
				<Card>
					<CardContent className="flex flex-col items-start gap-3 pt-6">
						<p role="alert" className="text-sm text-destructive">
							Could not load store orders.
						</p>
						<Button variant="outline" onClick={() => query.refetch()}>
							Retry
						</Button>
					</CardContent>
				</Card>
			)}

			{query.data && orders.length === 0 && (
				<Card>
					<CardContent className="pt-6 text-sm text-muted-foreground">
						{tab === "pending" ? "Nothing waiting for you right now." : "No orders in this view."}
					</CardContent>
				</Card>
			)}

			<div className="space-y-3">
				{orders.map((order) => {
					const cd = formatCountdown(order.storeOfferExpiresAt);
					const busy = inFlight === order.id;
					const pending = order.storeAcceptedAt === null && order.storeOfferedAt !== null;

					return (
						<Card key={order.id}>
							<CardHeader className="pb-3">
								<div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
									<div>
										<CardTitle className="text-base">{order.orderCode}</CardTitle>
										<p className="text-sm text-muted-foreground">
											{order.lineCount} item{order.lineCount === 1 ? "" : "s"}
											{order.offerLevel === "line" && (
												<> &middot; {order.storeLineCount} for your store</>
											)}
											{order.totalWithShip && (
												<>
													{" "}
													&middot; {order.totalWithShip} {order.currency ?? ""}
												</>
											)}
										</p>
									</div>
									<div className="flex items-center gap-2">
										{order.offerLevel === "line" && <Badge variant="outline">Split order</Badge>}
										{order.storeAcceptedAt !== null && <Badge variant="secondary">Accepted</Badge>}
										{pending && (
											<span
												className={`flex items-center gap-1 text-sm font-medium ${countdownClass(cd.urgency)}`}
											>
												<RiTimeLine className="size-4" aria-hidden />
												{cd.label}
											</span>
										)}
									</div>
								</div>
							</CardHeader>

							<CardContent className="space-y-3">
								{pending && order.offerLevel === "order" && (
									<div className="flex flex-col gap-2 sm:flex-row">
										<Button onClick={() => accept(order)} disabled={busy} className="sm:w-auto">
											{busy && <RiLoader4Line className="mr-1 size-4 animate-spin" aria-hidden />}
											Accept
										</Button>
										<Button
											variant="outline"
											onClick={() => setDeclineTarget(order)}
											disabled={busy}
										>
											Decline
										</Button>
									</div>
								)}

								<Button
									variant="ghost"
									size="sm"
									onClick={() => setExpanded(expanded === order.id ? null : order.id)}
								>
									{expanded === order.id ? "Hide items" : "Show items"}
								</Button>

								{expanded === order.id && (
									<OrderLines
										orderId={order.id}
										scopeQs={scopeQs}
										onAcceptLine={acceptLine}
										inFlight={inFlight}
									/>
								)}
							</CardContent>
						</Card>
					);
				})}
			</div>

			<AlertDialog
				open={declineTarget !== null}
				onOpenChange={(open) => !open && setDeclineTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Decline this order?</AlertDialogTitle>
						<AlertDialogDescription>
							This order will be offered to another store. You cannot undo this yourself.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Keep it</AlertDialogCancel>
						<AlertDialogAction onClick={() => declineTarget && decline(declineTarget)}>
							Decline
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

/**
 * All lines of an order. Lines belonging to ANOTHER store render greyed and
 * non-actionable but always visible: that is the "clear link to the rest of the
 * order" requirement.
 */
function OrderLines({
	orderId,
	scopeQs,
	onAcceptLine,
	inFlight,
}: {
	orderId: string;
	scopeQs: string;
	onAcceptLine: (orderId: string, lineId: string) => void;
	inFlight: string | null;
}) {
	const query = $api.useQuery("get", `/api/v1/store/orders/${orderId}${scopeQs}` as never);
	const detail = query.data as OrderDetail | undefined;

	if (query.isLoading) return <Skeleton className="h-20 w-full" />;
	// A failed detail fetch used to return null, so "Show items" expanded into
	// nothing and looked like an order with no lines. Say what happened.
	if (query.isError)
		return (
			<p role="alert" className="rounded-md border p-3 text-sm text-destructive">
				Could not load the items for this order.
			</p>
		);
	if (!detail) return null;

	return (
		<div className="space-y-2 rounded-md border p-3">
			{detail.lines.map((line) => {
				const mine = line.isThisStore;
				const cd = formatCountdown(line.storeOfferExpiresAt);
				const busy = inFlight === line.id;
				const pending = mine && line.storeOfferedAt && !line.storeAcceptedAt;

				return (
					<div
						key={line.id}
						className={`flex flex-col gap-2 border-b pb-2 last:border-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between ${
							mine ? "" : "opacity-60"
						}`}
					>
						<div className="min-w-0">
							<p className="truncate font-mono text-xs">{line.barcode}</p>
							<p className="text-sm">
								Size {line.size}
								{!mine && (
									<span className="text-muted-foreground">
										{" "}
										&middot; {line.storeName ?? "another store"}
									</span>
								)}
							</p>
						</div>
						<div className="flex items-center gap-2">
							{!mine && <Badge variant="outline">Other store</Badge>}
							{line.storeAcceptedAt && <Badge variant="secondary">Accepted</Badge>}
							{pending && (
								<>
									<span className={`text-xs font-medium ${countdownClass(cd.urgency)}`}>
										{cd.label}
									</span>
									<Button size="sm" onClick={() => onAcceptLine(orderId, line.id)} disabled={busy}>
										{busy && <RiLoader4Line className="mr-1 size-4 animate-spin" aria-hidden />}
										Accept
									</Button>
								</>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
}
